<template>
  <div>
    <b-modal
      v-model="showConfigModal"
      :title="flow.modalTitle"
      size="lg"
      centered
      hide-footer
      modal-class="exercise-generator-modal"
      @show="handleOpen"
    >
      <div class="exercise-flow">
        <transition name="exercise-gen-overlay">
          <div
            v-if="generating"
            class="exercise-generation-overlay"
          >
            <div class="exercise-generation-card">
              <div class="exercise-gen-spinner-wrap">
                <div class="exercise-gen-spinner" />
                <div class="exercise-gen-spinner-glow" />
              </div>
              <h4 class="exercise-gen-title">
                {{ flow.preparing }} {{ selectedFormatOption.label.toLowerCase() }}
              </h4>
              <p class="exercise-gen-subtitle">
                {{ flow.source }}: {{ selectedSource ? selectedSource.label : flow.courseFallback }} ·
                {{ flow.level }}: {{ selectedDifficultyOption.label }}
              </p>
              <div class="exercise-gen-dots">
                <span class="exercise-gen-dot" />
                <span class="exercise-gen-dot" />
                <span class="exercise-gen-dot" />
              </div>
            </div>
          </div>
        </transition>

        <div class="exercise-flow-head">
          <div>
            <h4 class="exercise-flow-title">
              {{ flow.flowTitle }}
            </h4>
            <p class="exercise-flow-subtitle">
              {{ flow.flowSubtitle }}
            </p>
          </div>
          <div class="exercise-flow-cost">
            <span>⚡ {{ currentCost }}</span>
          </div>
        </div>

        <section class="exercise-flow-section">
          <div class="exercise-section-head">
            <span class="exercise-step">1</span>
            <div>
              <h5>{{ flow.step1Title }}</h5>
              <p>{{ flow.step1Desc }}</p>
            </div>
          </div>

          <div
            v-if="loadingSources"
            class="exercise-source-loading"
          >
            {{ flow.loadingSources }}
          </div>

          <div
            v-else
            class="exercise-source-grid"
          >
            <button
              v-for="source in sourceOptions"
              :key="source.id"
              type="button"
              class="exercise-choice-card"
              :class="{ active: selectedSourceId === source.id }"
              @click="selectedSourceId = source.id"
            >
              <div class="exercise-choice-top">
                <span class="exercise-choice-badge">{{ source.badge }}</span>
                <span class="exercise-choice-icon">{{ source.emoji }}</span>
              </div>
              <div class="exercise-choice-title">
                {{ source.label }}
              </div>
              <div class="exercise-choice-desc">
                {{ source.description }}
              </div>
            </button>
          </div>
        </section>

        <section class="exercise-flow-section">
          <div class="exercise-section-head">
            <span class="exercise-step">2</span>
            <div>
              <h5>{{ flow.step2Title }}</h5>
              <p>{{ flow.step2Desc }}</p>
            </div>
          </div>

          <div class="exercise-choice-grid difficulty-grid">
            <button
              v-for="difficulty in difficultyOptions"
              :key="difficulty.id"
              type="button"
              class="exercise-choice-card small"
              :class="{ active: selectedDifficulty === difficulty.id }"
              @click="selectedDifficulty = difficulty.id"
            >
              <div class="exercise-choice-title">
                {{ difficulty.label }}
              </div>
              <div class="exercise-choice-desc">
                {{ difficulty.description }}
              </div>
            </button>
          </div>
        </section>

        <section class="exercise-flow-section">
          <div class="exercise-section-head">
            <span class="exercise-step">3</span>
            <div>
              <h5>{{ flow.step3Title }}</h5>
              <p>{{ flow.step3Desc }}</p>
            </div>
          </div>

          <div class="exercise-choice-grid format-grid">
            <button
              v-for="format in formatOptions"
              :key="format.id"
              type="button"
              class="exercise-choice-card format-card"
              :class="{ active: selectedFormat === format.id }"
              @click="selectedFormat = format.id"
            >
              <div class="exercise-choice-top">
                <span class="exercise-choice-icon">{{ format.emoji }}</span>
                <span class="exercise-choice-badge light">{{ format.short }}</span>
              </div>
              <div class="exercise-choice-title">
                {{ format.label }}
              </div>
              <div class="exercise-choice-desc">
                {{ format.description }}
              </div>
            </button>
          </div>
        </section>

        <div class="exercise-flow-summary">
          <div>
            <div class="exercise-summary-kicker">
              {{ flow.recapKicker }}
            </div>
            <div class="exercise-summary-title">
              {{ selectedFormatOption.label }} · {{ selectedDifficultyOption.label }}
            </div>
            <p class="exercise-summary-subtitle">
              {{ selectedSource ? selectedSource.label : flow.pickSource }}
            </p>
          </div>
          <div class="exercise-summary-stats">
            <span>{{ estimatedItemLabel }}</span>
            <span>⚡ {{ currentCost }} pts</span>
          </div>
        </div>

        <div class="exercise-future-note">
          {{ flow.futureNote }}
        </div>

        <div class="exercise-flow-actions">
          <button
            type="button"
            class="btn-flow-secondary"
            :disabled="generating"
            @click="showConfigModal = false"
          >
            {{ flow.cancel }}
          </button>
          <button
            type="button"
            class="btn-flow-primary"
            :disabled="generating || !selectedSource"
            @click="generateExercises"
          >
            <span
              v-if="generating"
              class="exercise-mini-loader"
            />
            <span>{{ generating ? flow.generatingBtn : flow.generateNow }}</span>
          </button>
        </div>
      </div>
    </b-modal>

    <b-modal
      v-model="showResultModal"
      :title="resultModalTitle"
      size="xl"
      centered
      hide-footer
      modal-class="exercise-result-modal"
      @hidden="resultData = null"
    >
      <ExerciseResultContent :result="resultData" />
    </b-modal>
  </div>
</template>

<script>
import { BModal } from 'bootstrap-vue'
import axios from '@axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { getToolCost } from '@/libs/creditsCosts'
import ExerciseResultContent from '@/components/ExerciseResultContent.vue'

const DEFAULT_FORMAT = 'guided_exercise'

export default {
  name: 'ExerciseGeneratorFlow',
  components: {
    BModal,
    ExerciseResultContent,
  },
  props: {
    courseId: {
      type: String,
      required: true,
    },
    courseTitle: {
      type: String,
      default: '',
    },
    courseSubject: {
      type: String,
      default: '',
    },
    manualText: {
      type: String,
      default: '',
    },
    createdBy: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      showConfigModal: false,
      showResultModal: false,
      loadingSources: false,
      generating: false,
      ragDocuments: [],
      selectedSourceId: 'course',
      selectedDifficulty: 'medium',
      selectedFormat: DEFAULT_FORMAT,
      resultData: null,
    }
  },
  computed: {
    isUiEn() {
      const l = String(this.$i18n?.locale || 'fr').toLowerCase()
      return l === 'en' || l.startsWith('en-')
    },
    flow() {
      if (this.isUiEn) {
        return {
          modalTitle: 'Generate exercises',
          preparing: 'Preparing',
          source: 'Source',
          level: 'Level',
          courseFallback: 'course',
          flowTitle: 'A guided flow for your review',
          flowSubtitle: 'Pick a source, a difficulty, then the format that best fits your study session.',
          step1Title: 'Choose the course or document',
          step1Desc: 'Select the source used to generate exercises.',
          loadingSources: 'Loading sources…',
          step2Title: 'Choose difficulty',
          step2Desc: 'Difficulty adjusts depth and the kind of effort required.',
          step3Title: 'Choose format',
          step3Desc: 'Short answer, guided exercise, or case study depending on how you want to practice.',
          recapKicker: 'Before you generate',
          pickSource: 'Choose a source',
          futureNote: 'Quizzes and flashcards stay available in their dedicated tools.',
          cancel: 'Cancel',
          generatingBtn: 'Preparing…',
          generateNow: 'Generate now',
          toastErrTitle: 'Error',
          toastOkTitle: 'OK',
          toastWarnTitle: 'Notice',
          resultDefaultTitle: 'Generated exercises',
          errPickSource: 'Pick a source to get started.',
          errManualShort: 'Pasted text is too short to generate exercises.',
          errDocText: 'The selected document does not contain enough usable text.',
          errNeedContent: 'Add or index more content before generating exercises.',
          errQuiz: 'Could not generate the quiz.',
          errFlashcards: 'Could not generate flashcards.',
          errExercises: 'Could not generate exercises.',
          creditsShort: (cur, req) => `You have ${cur} pts. You need ${req} pts to continue.`,
          creditsRecharge: 'Top up your points to continue.',
          savedToast: 'Exercise saved to the course list.',
          saveWarn: 'Exercise generated, but auto-save did not succeed.',
          genFailed: 'Generation failed',
          courseLabel: 'Course',
          fullCourse: 'Full course',
          courseDesc: 'Uses all indexed content from the course.',
          textBadge: 'Text',
          manualLabel: 'Text pasted in the studio',
          charsAvail: n => `${n} characters available immediately.`,
          docBadge: 'Document',
          indexedDoc: 'Indexed document',
          sourceFile: 'file',
        }
      }
      return {
        modalTitle: 'Generer des exercices',
        preparing: 'Preparation de',
        source: 'Source',
        level: 'Niveau',
        courseFallback: 'cours',
        flowTitle: 'Un flow guide pour tes revisions',
        flowSubtitle: 'Choisis une source, un niveau, puis le format le plus utile pour reviser ton cours.',
        step1Title: 'Choisir le cours ou le document',
        step1Desc: 'Selectionne la source qui servira a generer les exercices.',
        loadingSources: 'Chargement des sources...',
        step2Title: 'Choisir la difficulte',
        step2Desc: "Le niveau ajuste la profondeur et le type d'effort demande.",
        step3Title: 'Choisir le format',
        step3Desc: "Reponse courte, exercice guide ou etude de cas selon l'effort attendu.",
        recapKicker: 'Recap avant generation',
        pickSource: 'Choisis une source',
        futureNote: 'Les quiz et les flashcards restent disponibles dans leurs outils dedies.',
        cancel: 'Annuler',
        generatingBtn: 'Preparation en cours...',
        generateNow: 'Generer maintenant',
        toastErrTitle: 'Erreur',
        toastOkTitle: 'OK',
        toastWarnTitle: 'Attention',
        resultDefaultTitle: 'Exercices generes',
        errPickSource: 'Selectionne une source pour commencer.',
        errManualShort: 'Le texte colle est trop court pour generer des exercices.',
        errDocText: 'Le document selectionne ne contient pas assez de texte exploitable.',
        errNeedContent: 'Ajoute ou indexe plus de contenu avant de generer des exercices.',
        errQuiz: 'Impossible de generer le quiz.',
        errFlashcards: 'Impossible de generer les flashcards.',
        errExercises: 'Impossible de generer les exercices.',
        creditsShort: (cur, req) => `Tu as ${cur} pts. Il faut ${req} pts pour continuer.`,
        creditsRecharge: 'Recharge tes points pour continuer.',
        savedToast: 'Exercice sauvegarde dans la liste du cours.',
        saveWarn: "Exercice genere, mais la sauvegarde automatique n'a pas abouti.",
        genFailed: 'Generation impossible',
        courseLabel: 'Cours',
        fullCourse: 'Cours complet',
        courseDesc: 'Utilise tous les contenus indexes du cours.',
        textBadge: 'Texte',
        manualLabel: 'Texte colle dans le studio',
        charsAvail: n => `${n} caracteres disponibles immediatement.`,
        docBadge: 'Document',
        indexedDoc: 'Document indexe',
        sourceFile: 'fichier',
      }
    },
    difficultyOptions() {
      if (this.isUiEn) {
        return [
          { id: 'easy', label: 'Easy', description: 'Reinforce basics and get started quickly.' },
          { id: 'medium', label: 'Medium', description: 'Test understanding and active recall.' },
          { id: 'hard', label: 'Advanced', description: 'Push analysis and applied thinking.' },
        ]
      }
      return [
        { id: 'easy', label: 'Facile', description: 'Pour consolider les bases et demarrer vite.' },
        { id: 'medium', label: 'Intermediaire', description: 'Pour tester la comprehension et la memoire active.' },
        { id: 'hard', label: 'Avance', description: "Pour pousser l'analyse et l'application en profondeur." },
      ]
    },
    formatOptions() {
      if (this.isUiEn) {
        return [
          {
            id: 'short_answer',
            label: 'Short answer',
            short: 'Short',
            emoji: '✍️',
            description: 'Brief questions you answer in your own words.',
            toolCostKey: 'exercises',
          },
          {
            id: 'guided_exercise',
            label: 'Guided exercise',
            short: 'Guided',
            emoji: '🧭',
            description: 'A stepped exercise with helpful hints.',
            toolCostKey: 'exercises',
          },
          {
            id: 'case_study',
            label: 'Case study',
            short: 'Case',
            emoji: '🧪',
            description: 'A concrete scenario to analyze and solve.',
            toolCostKey: 'exercises',
          },
        ]
      }
      return [
        {
          id: 'short_answer',
          label: 'Reponse courte',
          short: 'Court',
          emoji: '✍️',
          description: 'Questions breves auxquelles tu dois repondre toi-meme.',
          toolCostKey: 'exercises',
        },
        {
          id: 'guided_exercise',
          label: 'Exercice guide',
          short: 'Guide',
          emoji: '🧭',
          description: 'Un exercice decoupe en etapes avec indices utiles.',
          toolCostKey: 'exercises',
        },
        {
          id: 'case_study',
          label: 'Etude de cas',
          short: 'Cas',
          emoji: '🧪',
          description: 'Une situation concrete a analyser et resoudre.',
          toolCostKey: 'exercises',
        },
      ]
    },
    sourceOptions() {
      const manual = (this.manualText || '').trim()
      const f = this.flow
      const options = [
        {
          id: 'course',
          badge: f.courseLabel,
          emoji: '📚',
          label: this.courseTitle || f.fullCourse,
          description: f.courseDesc,
          type: 'course',
        },
      ]

      if (manual.length >= 50) {
        options.unshift({
          id: 'manual',
          badge: f.textBadge,
          emoji: '📝',
          label: f.manualLabel,
          description: f.charsAvail(Math.min(manual.length, 9000)),
          type: 'manual',
        })
      }

      const docs = (this.ragDocuments || []).map(doc => ({
        id: `document:${doc.id}`,
        badge: f.docBadge,
        emoji: this.getDocumentEmoji(doc.fileType),
        label: doc.originalName || f.indexedDoc,
        description: `Source ${String(doc.fileType || f.sourceFile).toUpperCase()}${doc.status ? ` • ${doc.status}` : ''}`,
        type: 'document',
        documentId: doc.id,
      }))

      return [...options, ...docs]
    },
    selectedSource() {
      return this.sourceOptions.find(source => source.id === this.selectedSourceId) || null
    },
    selectedFormatOption() {
      return this.formatOptions.find(option => option.id === this.selectedFormat) || this.formatOptions[0]
    },
    selectedDifficultyOption() {
      return this.difficultyOptions.find(option => option.id === this.selectedDifficulty) || this.difficultyOptions[1]
    },
    currentCost() {
      return getToolCost(this.selectedFormatOption.toolCostKey)
    },
    estimatedItemLabel() {
      const count = this.getItemCount(this.selectedFormat, this.selectedDifficulty)
      if (this.isUiEn) {
        return `${count} ${count > 1 ? 'exercises' : 'exercise'} planned`
      }
      return `${count} exercice${count > 1 ? 's' : ''} prevu${count > 1 ? 's' : ''}`
    },
    resultModalTitle() {
      if (!this.resultData) return this.flow.resultDefaultTitle
      return this.resultData.title || this.flow.resultDefaultTitle
    },
  },
  watch: {
    courseId() {
      this.ragDocuments = []
      this.selectedSourceId = 'course'
      this.selectedFormat = DEFAULT_FORMAT
      this.resultData = null
    },
  },
  methods: {
    normalizeFormat(format) {
      if (this.formatOptions.some(option => option.id === format)) return format
      return DEFAULT_FORMAT
    },
    open(options = {}) {
      if (options.format) {
        this.selectedFormat = this.normalizeFormat(options.format)
      }
      if (options.difficulty) {
        this.selectedDifficulty = options.difficulty
      }
      this.showConfigModal = true
    },
    async handleOpen() {
      this.selectedFormat = this.normalizeFormat(this.selectedFormat)
      await this.loadSources()
    },
    toastError(text) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: this.flow.toastErrTitle,
          icon: 'AlertCircleIcon',
          text,
          variant: 'danger',
        },
      })
    },
    toastSuccess(text) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: this.flow.toastOkTitle,
          icon: 'CheckIcon',
          text,
          variant: 'success',
        },
      })
    },
    toastWarning(text) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: this.flow.toastWarnTitle,
          icon: 'AlertTriangleIcon',
          text,
          variant: 'warning',
        },
      })
    },
    getDocumentEmoji(fileType) {
      const type = String(fileType || '').toLowerCase()
      if (type === 'pdf') return '📄'
      if (type === 'docx' || type === 'doc') return '🗂'
      if (type === 'ppt' || type === 'pptx') return '📊'
      if (type === 'txt') return '📝'
      return '📁'
    },
    getItemCount(format, difficulty) {
      const countMap = {
        easy: { quiz: 5, flashcards: 6, default: 2 },
        medium: { quiz: 6, flashcards: 8, default: 3 },
        hard: { quiz: 8, flashcards: 10, default: 4 },
      }
      const difficultyMap = countMap[difficulty] || countMap.medium
      return difficultyMap[format] || difficultyMap.default
    },
    async loadSources() {
      this.loadingSources = true
      try {
        const res = await axios.get(`/api/rag/courses/${this.courseId}/documents`)
        this.ragDocuments = res.data?.documents || []
      } catch (error) {
        console.error('Error loading exercise sources:', error)
        this.ragDocuments = []
      } finally {
        if (!this.sourceOptions.some(source => source.id === this.selectedSourceId)) {
          this.selectedSourceId = this.sourceOptions[0] ? this.sourceOptions[0].id : 'course'
        }
        this.loadingSources = false
      }
    },
    async resolveSourceText() {
      const source = this.selectedSource
      if (!source) {
        throw new Error(this.flow.errPickSource)
      }

      if (source.type === 'manual') {
        const text = (this.manualText || '').trim()
        if (text.length < 50) {
          throw new Error(this.flow.errManualShort)
        }
        return text.substring(0, 9000)
      }

      if (source.type === 'document' && source.documentId) {
        const res = await axios.get(`/api/rag/courses/${this.courseId}/compiled-text?documentId=${source.documentId}&limitChars=9000`)
        const text = (res.data?.text || '').trim()
        if (text.length < 80) {
          throw new Error(this.flow.errDocText)
        }
        return text.substring(0, 9000)
      }

      const res = await axios.get(`/api/rag/courses/${this.courseId}/compiled-text?limitChars=9000`)
      const text = (res.data?.text || '').trim()
      if (text.length < 120) {
        throw new Error(this.flow.errNeedContent)
      }
      return text.substring(0, 9000)
    },
    async handleInsufficientCredits(error) {
      if (error?.response?.status !== 402) return false
      const required = error.response?.data?.required
      const current = error.response?.data?.current
      const message = (typeof required === 'number' && typeof current === 'number')
        ? this.flow.creditsShort(current, required)
        : this.flow.creditsRecharge
      await this.$store.dispatch('credits/openRechargeModal', { message }).catch(() => {})
      return true
    },
    async saveGeneratedArtifact(resultData) {
      if (!this.createdBy || !resultData) return null

      try {
        const res = await axios.post('/study-artifacts', {
          type: 'exercises',
          courseId: this.courseId,
          createdBy: this.createdBy,
          creatorRole: 'student',
          visibility: 'private',
          title: resultData.title || this.flow.resultDefaultTitle,
          contentJson: resultData,
          meta: {
            exerciseFormat: resultData.formatLabel || null,
            exerciseDifficulty: resultData.difficultyLabel || null,
            exerciseSourceLabel: resultData.sourceLabel || null,
            itemCount: Array.isArray(resultData.items) ? resultData.items.length : 0,
          },
        })

        return res.data?.artifact || null
      } catch (error) {
        console.error('Error saving generated exercises:', error)
        return null
      }
    },
    async generateExercises() {
      if (!this.selectedSource) return

      this.generating = true
      try {
        const text = await this.resolveSourceText()
        const count = this.getItemCount(this.selectedFormat, this.selectedDifficulty)
        const title = `${this.courseTitle || this.flow.courseLabel} - ${this.selectedFormatOption.label}`

        if (this.selectedFormat === 'quiz') {
          const res = await axios.post('/api/ai/quiz/from-text', {
            text,
            title,
            count,
            difficulty: this.selectedDifficulty,
          })
          const data = res.data?.data
          if (!data) throw new Error(this.flow.errQuiz)
          this.showConfigModal = false
          this.$emit('open-quiz', data)
        } else if (this.selectedFormat === 'flashcards') {
          const res = await axios.post('/api/ai/flashcards/from-text', {
            text,
            title,
            count,
            difficulty: this.selectedDifficulty,
          })
          const data = res.data?.data
          if (!data) throw new Error(this.flow.errFlashcards)
          this.showConfigModal = false
          this.$emit('open-flashcards', data)
        } else {
          const res = await axios.post('/api/ai/exercises/from-text', {
            text,
            title,
            difficulty: this.selectedDifficulty,
            format: this.selectedFormat,
            count,
          })

          const data = res.data?.data
          if (!data) throw new Error(this.flow.errExercises)

          const resultData = {
            ...data,
            sourceLabel: this.selectedSource.label,
            difficultyLabel: this.selectedDifficultyOption.label,
            formatLabel: this.selectedFormatOption.label,
          }

          const savedArtifact = await this.saveGeneratedArtifact(resultData)

          this.resultData = resultData
          this.showConfigModal = false
          this.showResultModal = true

          if (savedArtifact) {
            this.$emit('artifact-saved', savedArtifact)
            this.toastSuccess(this.flow.savedToast)
          } else if (this.createdBy) {
            this.toastWarning(this.flow.saveWarn)
          }
        }

        this.$emit('generated', {
          format: this.selectedFormat,
          difficulty: this.selectedDifficulty,
          source: this.selectedSource.id,
        })
        this.$store.dispatch('credits/fetchBalance').catch(() => {})
      } catch (error) {
        console.error('Error generating guided exercises:', error)
        const handled = await this.handleInsufficientCredits(error)
        if (handled) return
        this.toastError(error.response?.data?.error || error.message || this.flow.genFailed)
      } finally {
        this.generating = false
      }
    },
  },
}
</script>

<style>
.exercise-generator-modal .modal-content,
.exercise-result-modal .modal-content {
  border: none;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 24px 80px rgba(15, 23, 42, 0.18);
}

.exercise-generator-modal .modal-header,
.exercise-result-modal .modal-header {
  border-bottom: 1px solid rgba(15, 23, 42, 0.08);
  padding: 18px 20px;
}

.exercise-generator-modal .modal-title,
.exercise-result-modal .modal-title {
  font-weight: 900;
  color: #0f172a;
}

.exercise-generator-modal .modal-body,
.exercise-result-modal .modal-body {
  padding: 20px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
}

.exercise-flow {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.exercise-flow-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
}

.exercise-flow-title {
  margin: 0;
  font-size: 1.08rem;
  font-weight: 900;
  color: #0f172a;
}

.exercise-flow-subtitle {
  margin: 8px 0 0 0;
  font-size: 0.92rem;
  line-height: 1.6;
  color: #64748b;
}

.exercise-flow-cost {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 72px;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(56, 182, 255, 0.12);
  color: #0284c7;
  font-weight: 800;
}

.exercise-flow-section {
  padding: 16px;
  border-radius: 18px;
  background: #ffffff;
  border: 1px solid rgba(15, 23, 42, 0.06);
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.04);
}

.exercise-section-head {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 14px;
}

.exercise-section-head h5 {
  margin: 0;
  font-size: 0.98rem;
  font-weight: 900;
  color: #0f172a;
}

.exercise-section-head p {
  margin: 6px 0 0 0;
  color: #64748b;
  font-size: 0.86rem;
  line-height: 1.5;
}

.exercise-step {
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  background: linear-gradient(135deg, #38b6ff, #7367f0);
  color: #ffffff;
  font-weight: 900;
  flex-shrink: 0;
}

.exercise-source-loading {
  padding: 18px;
  border-radius: 14px;
  background: rgba(59, 130, 246, 0.08);
  color: #1d4ed8;
  font-weight: 700;
}

.exercise-source-grid,
.exercise-choice-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.format-grid {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.exercise-choice-card {
  width: 100%;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 16px;
  background: #ffffff;
  text-align: left;
  padding: 14px;
  cursor: pointer;
  transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
}

.exercise-choice-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 12px 24px rgba(15, 23, 42, 0.08);
}

.exercise-choice-card.active {
  border-color: rgba(56, 182, 255, 0.45);
  box-shadow: 0 0 0 3px rgba(56, 182, 255, 0.12);
  background: linear-gradient(180deg, rgba(56, 182, 255, 0.06), #ffffff);
}

.exercise-choice-card.small {
  min-height: 120px;
}

.exercise-choice-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 12px;
}

.exercise-choice-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 9px;
  border-radius: 999px;
  background: rgba(255, 196, 0, 0.18);
  color: #8a5a00;
  font-size: 0.72rem;
  font-weight: 800;
}

.exercise-choice-badge.light {
  background: rgba(56, 182, 255, 0.12);
  color: #0284c7;
}

.exercise-choice-icon {
  font-size: 1.1rem;
}

.exercise-choice-title {
  font-size: 0.95rem;
  font-weight: 900;
  color: #0f172a;
}

.exercise-choice-desc {
  margin-top: 7px;
  color: #64748b;
  font-size: 0.82rem;
  line-height: 1.5;
}

.exercise-future-note {
  padding: 14px 16px;
  border-radius: 16px;
  background: linear-gradient(135deg, rgba(11, 163, 255, 0.08), rgba(255, 196, 0, 0.10));
  color: #0f172a;
  font-size: 0.86rem;
  font-weight: 700;
}

.exercise-flow-summary {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 14px;
  padding: 16px 18px;
  border-radius: 18px;
  background: linear-gradient(135deg, rgba(56, 182, 255, 0.08), rgba(115, 103, 240, 0.08));
  border: 1px solid rgba(56, 182, 255, 0.14);
}

.exercise-summary-kicker {
  font-size: 0.74rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #0284c7;
}

.exercise-summary-title {
  margin-top: 4px;
  font-size: 0.98rem;
  font-weight: 900;
  color: #0f172a;
}

.exercise-summary-subtitle {
  margin: 6px 0 0 0;
  color: #475569;
  font-size: 0.84rem;
}

.exercise-summary-stats {
  display: inline-flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  font-size: 0.8rem;
  font-weight: 800;
  color: #1e293b;
  white-space: nowrap;
}

.exercise-generation-overlay {
  position: absolute;
  inset: 0;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  border-radius: 22px;
  background: rgba(248, 251, 255, 0.84);
  backdrop-filter: blur(10px);
}

.exercise-generation-card {
  width: min(100%, 420px);
  background: #fff;
  border-radius: 24px;
  padding: 30px 28px;
  text-align: center;
  box-shadow: 0 24px 64px rgba(15, 23, 42, 0.16);
  border: 1px solid rgba(255, 255, 255, 0.6);
}

.exercise-gen-spinner-wrap {
  position: relative;
  width: 64px;
  height: 64px;
  margin: 0 auto 20px;
}

.exercise-gen-spinner {
  position: absolute;
  inset: 0;
  border: 3px solid rgba(115, 103, 240, 0.15);
  border-top-color: #7367f0;
  border-radius: 50%;
  animation: exerciseSpin 1s linear infinite;
}

.exercise-gen-spinner-glow {
  position: absolute;
  inset: -4px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(115, 103, 240, 0.15) 0%, transparent 70%);
  animation: exerciseGlow 2s ease-in-out infinite;
}

.exercise-gen-title {
  margin: 0 0 8px 0;
  font-size: 1.1rem;
  font-weight: 900;
  color: #0f172a;
}

.exercise-gen-subtitle {
  margin: 0 0 14px 0;
  font-size: 0.9rem;
  line-height: 1.6;
  color: #64748b;
}

.exercise-gen-dots {
  display: flex;
  justify-content: center;
  gap: 6px;
}

.exercise-gen-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #7367f0;
  animation: exerciseDotBounce 1.4s ease-in-out infinite both;
}

.exercise-gen-dot:nth-child(1) { animation-delay: 0s; }
.exercise-gen-dot:nth-child(2) { animation-delay: 0.2s; }
.exercise-gen-dot:nth-child(3) { animation-delay: 0.4s; }

.exercise-flow-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  flex-wrap: wrap;
}

.btn-flow-secondary,
.btn-flow-primary {
  border-radius: 12px;
  padding: 11px 16px;
  font-weight: 800;
  border: none;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.btn-flow-secondary {
  background: #eef2f7;
  color: #334155;
}

.btn-flow-primary {
  background: linear-gradient(135deg, #38b6ff, #7367f0);
  color: #ffffff;
  box-shadow: 0 12px 26px rgba(56, 182, 255, 0.22);
}

.btn-flow-secondary:disabled,
.btn-flow-primary:disabled {
  cursor: not-allowed;
  opacity: 0.65;
  box-shadow: none;
}

.exercise-mini-loader {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: #ffffff;
  animation: exerciseSpin 0.8s linear infinite;
}

@keyframes exerciseSpin {
  to { transform: rotate(360deg); }
}

@keyframes exerciseGlow {
  0%, 100% { opacity: 0.6; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.05); }
}

@keyframes exerciseDotBounce {
  0%, 80%, 100% { transform: scale(0.6); opacity: 0.5; }
  40% { transform: scale(1); opacity: 1; }
}

.exercise-result-wrap {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.exercise-result-hero {
  padding: 18px;
  border-radius: 18px;
  background: linear-gradient(135deg, rgba(56, 182, 255, 0.10), rgba(115, 103, 240, 0.10));
  border: 1px solid rgba(56, 182, 255, 0.10);
}

.exercise-result-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}

.result-tag {
  display: inline-flex;
  align-items: center;
  padding: 5px 10px;
  border-radius: 999px;
  background: #ffffff;
  color: #0f172a;
  font-size: 0.75rem;
  font-weight: 800;
}

.result-tag.muted {
  color: #475569;
}

.exercise-result-intro {
  margin: 0;
  color: #1e293b;
  line-height: 1.65;
  font-size: 0.92rem;
}

.exercise-result-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.exercise-result-item {
  background: #ffffff;
  border: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 18px;
  padding: 18px;
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.04);
}

.exercise-result-item-head {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 12px;
}

.exercise-result-item-head h5 {
  margin: 0;
  font-size: 1rem;
  font-weight: 900;
  color: #0f172a;
}

.exercise-item-index {
  font-size: 0.74rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #0284c7;
}

.exercise-item-context {
  margin: 0 0 12px 0;
  color: #475569;
  font-size: 0.88rem;
  line-height: 1.6;
}

.exercise-item-block + .exercise-item-block {
  margin-top: 14px;
}

.exercise-item-label {
  display: inline-flex;
  align-items: center;
  margin-bottom: 8px;
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #64748b;
}

.exercise-item-text {
  margin: 0;
  color: #0f172a;
  line-height: 1.7;
  white-space: pre-line;
}

.exercise-item-list {
  margin: 0;
  padding-left: 18px;
  color: #0f172a;
}

.exercise-item-list li + li {
  margin-top: 6px;
}

.exercise-item-list.ordered {
  padding-left: 20px;
}

.exercise-item-block.answer {
  padding: 14px;
  border-radius: 14px;
  background: rgba(56, 182, 255, 0.06);
  border: 1px solid rgba(56, 182, 255, 0.12);
}

@media (max-width: 767px) {
  .exercise-flow-head {
    flex-direction: column;
  }

  .exercise-source-grid,
  .exercise-choice-grid,
  .format-grid {
    grid-template-columns: 1fr;
  }

  .exercise-flow-actions {
    flex-direction: column-reverse;
  }

  .btn-flow-secondary,
  .btn-flow-primary {
    width: 100%;
    justify-content: center;
  }

  .exercise-flow-summary {
    flex-direction: column;
  }

  .exercise-summary-stats {
    align-items: flex-start;
    white-space: normal;
  }
}
</style>
